import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { forwardRef, useImperativeHandle } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import Select from "react-select";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import "./RegistrationForm.css";
import { NavLink } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useFormContext } from "./RegistrationFormContext";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import DropdownWithCustomValuesModal from "../../components/DropdownWithCustomValuesModal";
import StepSuccessModal from "./StepSuccessModal";
import RegStepItemDeleteInfoModal from "./RegStepItemDeleteInfoModal";

const countryPhoneLengths = {
  PK: 10,
  SA: 9,
};

const Step1Contacts = forwardRef(({ isOpen, moveToNextStep }, ref) => {
  const token = localStorage.getItem("token");
  const roleId = localStorage.getItem("roleId");
  const organizationId = localStorage.getItem("organizationId");
  const centerId = localStorage.getItem("centerId");
  const { sharedData, setSharedData } = useFormContext();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const {
    register,
    watch,
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      studentFamilyInformationDtos: [],
      currentContact: {
        id: 0,
        titleId: "",
        title: "",
        firstName: "",
        lastName: "",
        relationShipId: "",
        relationShip: "",
        nationalityId: "",
        nationality: "",
        jobTitle: "",
        guardianEmployersName: "",
        guardianHomeTel: "",
        email: "",
        mobileNumber: "",
        streetName: "",
        phone: "",
        address: "",
        guardianIdentityNo: "",
        isDismissleApp: false,
        isPrimary: false,
        isPupilsHome: false,
        vehicleRegistrationNumber: "",
        code: "",
        district: "",
        countryId: "",
        stateId: 0,
        cityId: 0,
        stateName: "",
        cityName: "",
        createdBy: localStorage.getItem("userId"),
        updatedBy: localStorage.getItem("userId"),
        userId: sharedData.userId,
        organizationId: organizationId,
        centerId: centerId,
        code: "",
      },
    },
  });

  const [lastAddedIndex, setLastAddedIndex] = useState(null);
  const [activityCategoryData, setActivityCategoryData] = useState([]);
  const [activityVenueData, setActivityVenueData] = useState([]);
  const [editingContactIndex, setEditingContactIndex] = useState(null);
  const [relationshipData, setRelationshipData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Contacts Field Array
  const {
    fields: studentFamilyInformationDtos,
    append: appendContact,
    remove: removeContact,
    update: updateContact,
  } = useFieldArray({
    control,
    name: "studentFamilyInformationDtos",
    keyName: "rhfId",
  });

  const getContactsList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    if (!sharedData.userId) {
      console.error("No valid userId available");
      return;
    }

    setIsLoading(true);

    // Use userIdFromUrl for campus officer (roleId === "3"), otherwise use sharedData.userId
    const userId =
      roleId === "3" ? sharedData.userIdFromUrl : sharedData.userId;

    try {
      const response = await get(
        `Student/GetStudentFamilyInformationByUserId?userId=${userId}`,
        // `Student/GetStudentFamilyInformationByUserId?userId=${sharedData.userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response && response.length > 0) {
        const mappedContacts = response.map((contact) => ({
          contactId: contact.id,
          rhfId: contact.id,
          relationShip: contact.relationShipId,
          title: contact.titleId,
          firstName: contact.firstName,
          lastName: contact.lastName,
          nationality: contact.nationalityId,
          jobTitle: contact.jobTitle,
          guardianEmployersName: contact.guardianEmployersName,
          guardianHomeTel: contact.guardianHomeTel,
          mobileNumber: contact.mobileNumber,
          email: contact.email,
          countryId: contact.countryId,
          // stateId: contact.stateId,
          // cityId: contact.cityId,
          stateId: 0,
          cityId: 0,
          stateName: contact.stateName,
          cityName: contact.cityName,
          district: contact.district,
          address: contact.address,
          isDismissleApp: contact.isDismissleApp,
          isPrimary: contact.isPrimary,
          vehicleRegistrationNumber: contact.vehicleRegistrationNumber,
          organizationId: organizationId,
          centerId: centerId,
          code: contact.code,
        }));

        // Set the initial values for the field array
        setValue("studentFamilyInformationDtos", mappedContacts);

        setSharedData((prev) => ({ ...prev, code: response[0].code }));
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (
      isOpen &&
      sharedData.userId &&
      (roleId !== "3" || sharedData.userIdFromUrl)
    ) {
      getContactsList();
      fetchCommonSettings("relationShip", setRelationshipData);
      fetchCommonSettings("title", setTitleData);
      fetchCommonSettings("nationality", setNationalityData);
      fetchCommonSettings("district", setDistrictData);
    }
  }, [isOpen, sharedData.userId, sharedData.userIdFromUrl, roleId]);
  // Dropdowns data start

  const [titleData, setTitleData] = useState([]);
  const [nationalityData, setNationalityData] = useState([]);
  const [districtData, setDistrictData] = useState([]);
  const [customDropdownName, setCustomDropdownName] = useState("");
  const [
    isCustomDropdownValuesModalVisible,
    setIsCustomDropdownValuesModalVisible,
  ] = useState(false);
  const fetchCommonSettings = async (moduleName, setData) => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=${moduleName}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setData(response?.commonSettingDetail);
    } catch (error) {
      console.error(`Error fetching ${moduleName}:`, error);
    }
  };

  useEffect(() => {
    if (nationalityData.length > 0) {
      const saudiOption = nationalityData.find(
        (option) =>
          option.value?.toLowerCase() === "saudi" ||
          option.name?.toLowerCase() === "saudi"
      );
      setValue(
        "currentContact.nationality",
        saudiOption
          ? saudiOption.id.toString()
          : nationalityData[0].id.toString(),
        { shouldValidate: true }
      );
    }
  }, [nationalityData, setValue]);

  // Dropdowns data end

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const selectedCountry = watch("currentContact.countryId");
  const selectedState = watch("currentContact.stateId");

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await get("Dropdown/GetCountries", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setCountries(response);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    fetchCountries();
  }, [token]);

  useEffect(() => {
    if (countries.length > 0) {
      setValue("currentContact.countryId", countries[0].id);
    }
  }, [countries, setValue]);

  // API call for saving studentFamilyInformationDtos
  const saveContacts = async (payload) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await post(
        "Student/UpdateStudentFamilyInformation",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        getContactsList();
        // toast.success("Success!");
        return true;
      } else {
        toast.error(response.message || "An unexpected error occurred");
        return false;
      }
    } catch (error) {
      console.error("Error.", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleContact = async () => {
    const isValid = await trigger("currentContact");
    if (!isValid) return;

    const currentContact = getValues("currentContact");

    const primaryContactExists = studentFamilyInformationDtos.some(
      (contact, index) => contact.isPrimary && index !== editingContactIndex
    );

    if (currentContact.isPrimary && primaryContactExists) {
      toast.error("Only one primary contact is allowed.");
      return;
    }

    if (editingContactIndex !== null) {
      updateContact(editingContactIndex, {
        ...currentContact,
        contactId: studentFamilyInformationDtos[editingContactIndex].contactId,
      });
      setEditingContactIndex(null);
    } else {
      const newContact = {
        ...currentContact,
        contactId: 0,
        rhfId: uuidv4(),
      };
      appendContact(newContact);

      setLastAddedIndex(studentFamilyInformationDtos.length);
    }

    // Reset the currentContact for the next entry
    setValue("currentContact", {
      id: 0,
      contactId: 0,
      relationShip: "",
      title: "",
      firstName: "",
      lastName: "",
      nationality:
        nationalityData.length > 0
          ? nationalityData
              .find(
                (option) =>
                  option.value?.toLowerCase() === "saudi" ||
                  option.name?.toLowerCase() === "saudi"
              )
              ?.id.toString() || nationalityData[0].id.toString()
          : "",
      jobTitle: "",
      guardianEmployersName: "",
      guardianHomeTel: "",
      mobileNumber: "",
      email: "",
      countryId: countries.length > 0 ? countries[0].id : "",
      stateId: 0,
      cityId: 0,
      stateName: "",
      cityName: "",
      district: "",
      address: "",
      isPupilsHome: false,
      isPrimary: false,
      isDismissleApp: false,
      vehicleRegistrationNumber: "",
    });
  };

  const save = async () => {
    const currentContact = getValues("currentContact");

    const textFieldsToCheck = [
      "title",
      "firstName",
      "lastName",
      "email",
      "jobTitle",
      "guardianEmployersName",
      "guardianHomeTel",
      "mobileNumber",
      "district",
      "address",
    ];

    const isTextInputFilled = textFieldsToCheck.some(
      (field) =>
        currentContact[field] !== undefined &&
        currentContact[field] !== null &&
        String(currentContact[field]).trim() !== ""
    );

    if (isTextInputFilled) {
      const isValid = await trigger("currentContact");
      if (!isValid) {
        return false;
      }

      const primaryContactExists = studentFamilyInformationDtos.some(
        (contact, index) => contact.isPrimary && index !== editingContactIndex
      );

      if (currentContact.isPrimary && primaryContactExists) {
        toast.error("Only one primary contact is allowed.");
        return false;
      }

      await handleContact();
    }

    const contactData = getValues("studentFamilyInformationDtos");

    if (contactData.length === 0) {
      toast.error("Please add at least one contact before saving.");
      return false;
    }

    // if (contactData.length >= 2) {
    //   const primaryContactExists = contactData.some(
    //     (contact) => contact.isPrimary
    //   );
    //   if (!primaryContactExists) {
    //     toast.error("Please ensure at least one contact is marked as primary.");
    //     return false;
    //   }
    // }

    const payload = {
      studentFamilyInformationDtos: contactData.map((contact) => {
        const titleId = contact.title;
        const relationShipId = contact.relationShip;
        const nationalityId = contact.nationality;

        return {
          id: contact.contactId || 0,
          titleId: titleId,
          title:
            titleData.find((title) => title.id === Number(titleId))?.value ||
            "",
          firstName: contact.firstName,
          lastName: contact.lastName,
          relationShipId: relationShipId,
          relationShip:
            relationshipData.find((rel) => rel.id === Number(relationShipId))
              ?.value || "",
          nationalityId: nationalityId,
          nationality:
            nationalityData.find((nat) => nat.id === Number(nationalityId))
              ?.value || "",
          jobTitle: contact.jobTitle,
          guardianEmployersName: contact.guardianEmployersName,
          guardianHomeTel: contact.guardianHomeTel,
          email: contact.email,
          mobileNumber: contact.mobileNumber,
          streetName: contact.guardianAddress || "",
          phone: contact.mobileNumber,
          countryId: contact.countryId,
          stateId: 0,
          cityId: 0,
          stateName: contact.stateName,
          cityName: contact.cityName,
          district: contact.district,
          address: contact.address,
          isPupilsHome: contact.isPupilsHome || false,
          guardianIdentityNo: contact.guardianIdentityNo || "",
          isDismissleApp: contact.isDismissleApp || false,
          isPrimary: contact.isPrimary || false,
          vehicleRegistrationNumber: contact.vehicleRegistrationNumber || "",
          createdBy: contact.createdBy || localStorage.getItem("userId"),
          updatedBy: contact.updatedBy || localStorage.getItem("userId"),
          userId: roleId === "3" ? sharedData.userIdFromUrl : sharedData.userId,
          organizationId: organizationId,
          centerId: centerId,
          code: contact.code || "",
        };
      }),
    };

    const success = await saveContacts(payload);
    if (success) {
      setShowSuccessModal(true);
    }
    return success;
  };

  const canProceedToNextStep = async () => {
    const contactData = getValues("studentFamilyInformationDtos") || [];

    if (contactData.length < 2) {
      return false; // Let parent handle toast
    }

    const primaryContactExists = contactData.some(
      (contact) => contact.isPrimary
    );
    if (!primaryContactExists) {
      return false; // Let parent handle toast
    }

    return true;
  };

  // const canProceedToNextStep = async () => {
  //   const contactData = getValues("studentFamilyInformationDtos") || [];

  //   if (contactData.length < 2) {
  //     toast.error("Please add at least 2 contacts before proceeding.");
  //     return false;
  //   }

  //   const primaryContactExists = contactData.some(
  //     (contact) => contact.isPrimary
  //   );
  //   if (!primaryContactExists) {
  //     toast.error("Please ensure at least one contact is marked as primary.");
  //     return false;
  //   }

  //   return true;
  // };

  useImperativeHandle(ref, () => ({
    save,
    canProceedToNextStep,
    getValues,
  }));

  const handleDeleteContact = (index) => {
    const contact = studentFamilyInformationDtos[index];
    if (contact.isPrimary) {
      toast.error("Primary contact cannot be deleted.");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to delete this contact?"
    );
    if (!confirmed) return;

    removeContact(index);
    setShowInfoModal(true); // Show InfoModal after removal

    // If the deleted contact is being edited, reset the form
    if (editingContactIndex === index) {
      setValue("currentContact", {
        id: 0,
        contactId: 0,
        relationShip: "",
        title: "",
        firstName: "",
        lastName: "",
        nationality:
          nationalityData.length > 0
            ? nationalityData
                .find(
                  (option) =>
                    option.value?.toLowerCase() === "saudi" ||
                    option.name?.toLowerCase() === "saudi"
                )
                ?.id.toString() || nationalityData[0].id.toString()
            : "",
        jobTitle: "",
        guardianEmployersName: "",
        guardianHomeTel: "",
        mobileNumber: "",
        email: "",
        countryId: countries.length > 0 ? countries[0].id : "",
        stateId: 0,
        cityId: 0,
        stateName: "",
        cityName: "",
        district: "",
        address: "",
        isPupilsHome: false,
        isPrimary: false,
        isDismissleApp: false,
        vehicleRegistrationNumber: "",
      });
      setStates([]);
      setCities([]);
      setEditingContactIndex(null);
    }

    // If a contact before the one being edited is removed, adjust the editing index
    if (editingContactIndex > index) {
      setEditingContactIndex((prev) => prev - 1);
    }
  };

  const handleCustomDropdownSetting = (dropdownName) => {
    setCustomDropdownName(dropdownName);
    setIsCustomDropdownValuesModalVisible(true);
  };

  const handleModalAddAnother = () => {
    setShowSuccessModal(false);
  };

  const handleModalNavigateToPupils = () => {
    setShowSuccessModal(false);
    moveToNextStep(2);
  };

  return (
    <div className="form-step">
      {isLoading && (
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      )}

      <div className="">
        {studentFamilyInformationDtos.length > 0 && (
          <div className="mb-4">
            <h6>Added Contacts</h6>
            <div className="list-group">
              {studentFamilyInformationDtos.map((contact, index) => {
                const relationship = relationshipData.find(
                  (rel) => rel.id === Number(contact.relationShip)
                );

                const isEditing = editingContactIndex === index;
                const isLastAdded = lastAddedIndex === index;

                return (
                  <div
                    key={contact.rhfId}
                    className={`list-group-item d-flex justify-content-between align-items-center border-primary ${
                      isEditing ? "bg-primary-subtle" : ""
                    } ${isLastAdded ? "bg-primary-subtle" : ""}`}
                  >
                    <div className="fs-9">
                      {relationship ? relationship.value : ""} -{" "}
                      {contact.firstName} {contact.lastName}
                      {contact.isPrimary && (
                        <span className="badge bg-primary fs-10 ms-2">
                          Primary Contact
                        </span>
                      )}
                    </div>
                    <div className="d-flex gap-1 ms-auto">
                      <button
                        type="button"
                        className="btn py-1 px-2 btn-outline-primary"
                        onClick={() => {
                          const contactToEdit = { ...contact };
                          setValue("currentContact", {
                            ...contactToEdit,
                            contactId: contactToEdit.contactId,
                          });
                          setEditingContactIndex(index);
                        }}
                      >
                        <i className="fa fa-edit"></i>
                      </button>
                      <button
                        type="button"
                        className="btn py-1 px-2 btn-outline-primary"
                        onClick={() => handleDeleteContact(index)}
                      >
                        <i className="fa fa-trash"></i>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Contact Form */}
        <div className="row g-3">
          <div className="col-md-3">
            <label className="form-label form-label-new">
              Title <span className="text-danger fs-9">*</span>
            </label>
            <div className="d-flex gap-2">
              <div className="w-100">
                <select
                  className="form-select"
                  {...register("currentContact.title", {
                    required: "This field is required.",
                  })}
                >
                  <option value="">Select</option>
                  {titleData.map((title, index) => (
                    <option key={index} value={title.id}>
                      {title.value}
                    </option>
                  ))}
                </select>
                {errors.currentContact?.title && (
                  <small className="text-danger">
                    {errors.currentContact.title.message}
                  </small>
                )}
              </div>
              {["9", "10", "11"].includes(roleId) ? null : (
                <div>
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    style={{
                      padding: "5.5px 10px",
                    }}
                    onClick={() => handleCustomDropdownSetting("title")}
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="col-md-3">
            <label className="form-label form-label-new">
              First Name<span className="text-danger fs-9">*</span>
            </label>
            <input
              {...register("currentContact.firstName", {
                required: "This field is required.",
                maxLength: {
                  value: 75,
                  message: "Maximum of 75 characters allowed.",
                },
              })}
              type="text"
              className="form-control"
            />
            {errors.currentContact?.firstName && (
              <small className="text-danger">
                {errors.currentContact.firstName.message}
              </small>
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label form-label-new">
              Last Name<span className="text-danger fs-9">*</span>
            </label>
            <input
              {...register("currentContact.lastName", {
                required: "This field is required.",
                maxLength: {
                  value: 75,
                  message: "Maximum of 75 characters allowed.",
                },
              })}
              type="text"
              className="form-control"
            />
            {errors.currentContact?.lastName && (
              <small className="text-danger">
                {errors.currentContact.lastName.message}
              </small>
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label form-label-new">
              Relationship <span className="text-danger fs-9">*</span>
            </label>
            <div className="d-flex gap-2">
              <div className="w-100">
                <select
                  className="form-select"
                  {...register("currentContact.relationShip", {
                    required: "This field is required.",
                  })}
                >
                  <option value="">Select</option>
                  {relationshipData.map((relationship, index) => (
                    <option key={index} value={relationship.id}>
                      {relationship.value}
                    </option>
                  ))}
                </select>
                {errors.currentContact?.relationShip && (
                  <small className="text-danger">
                    {errors.currentContact.relationShip.message}
                  </small>
                )}
              </div>
              {["9", "10", "11"].includes(roleId) ? null : (
                <div>
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    style={{
                      padding: "5.5px 10px",
                    }}
                    onClick={() => handleCustomDropdownSetting("relationShip")}
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="col-md-3">
            <label className="form-label form-label-new">
              Nationality <span className="text-danger fs-9">*</span>
            </label>
            <div className="d-flex gap-2">
              <div className="w-100">
                <select
                  className="form-select"
                  {...register("currentContact.nationality", {
                    required: "This field is required.",
                  })}
                >
                  <option value="">Select</option>
                  {nationalityData.map((nationality, index) => (
                    <option key={index} value={nationality.id}>
                      {nationality.value}
                    </option>
                  ))}
                </select>
                {errors.currentContact?.nationality && (
                  <small className="text-danger">
                    {errors.currentContact.nationality.message}
                  </small>
                )}
              </div>
              {["9", "10", "11"].includes(roleId) ? null : (
                <div>
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    style={{
                      padding: "5.5px 10px",
                    }}
                    onClick={() => handleCustomDropdownSetting("nationality")}
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="col-md-3">
            <label className="form-label form-label-new">
              Job Title<span className="text-danger fs-9">*</span>
            </label>
            <input
              {...register("currentContact.jobTitle", {
                required: "This field is required.",
                maxLength: {
                  value: 75,
                  message: "Maximum of 75 characters allowed.",
                },
              })}
              type="text"
              className="form-control"
            />
            {errors.currentContact?.jobTitle && (
              <small className="text-danger">
                {errors.currentContact.jobTitle.message}
              </small>
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label form-label-new">
              Employer Name<span className="text-danger fs-9">*</span>
            </label>
            <input
              {...register("currentContact.guardianEmployersName", {
                required: "This field is required.",
                maxLength: {
                  value: 75,
                  message: "Maximum of 75 characters allowed.",
                },
              })}
              type="text"
              className="form-control"
            />
            {errors.currentContact?.guardianEmployersName && (
              <small className="text-danger">
                {errors.currentContact.guardianEmployersName.message}
              </small>
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label form-label-new">
              Guardian Home Tel
              <span className="text-danger fs-9">*</span>
            </label>
            <input
              {...register("currentContact.guardianHomeTel", {
                required: "This field is required.",
                maxLength: {
                  value: 75,
                  message: "Maximum of 75 characters allowed.",
                },
              })}
              type="text"
              className="form-control"
            />
            {errors.currentContact?.guardianHomeTel && (
              <small className="text-danger">
                {errors.currentContact.guardianHomeTel.message}
              </small>
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label form-label-new">
              Guardian Mobile No.
              <span className="text-danger fs-9">*</span>
            </label>
            <Controller
              name="currentContact.mobileNumber"
              control={control}
              rules={{
                required: "Phone number is required",
                validate: (value) => {
                  if (!value) return "Phone number is required";
                  const parsed = parsePhoneNumberFromString(value);
                  if (!parsed) {
                    return "Invalid phone number";
                  }
                  const countryCode = parsed.country;
                  const nationalNumber = parsed.nationalNumber;
                  const maxLength = countryPhoneLengths[countryCode];
                  if (maxLength && nationalNumber.length !== maxLength) {
                    return `Phone number must be exactly ${maxLength} digits for ${countryCode}`;
                  }
                  // Strict validation for maximum 12 digits
                  const totalDigits = value.replace(/[^0-9]/g, "").length;
                  if (totalDigits > 12) {
                    return "Phone number cannot exceed 12 digits";
                  }
                  return true;
                },
              }}
              render={({ field: { onChange, value } }) => (
                <PhoneInput
                  value={value}
                  onChange={(newValue) => {
                    // Restrict input to 12 digits during typing
                    const digitCount = (newValue || "").replace(
                      /[^0-9]/g,
                      ""
                    ).length;
                    if (!newValue || digitCount <= 12) {
                      onChange(newValue);
                    }
                  }}
                  defaultCountry="SA" // Set Saudi Arabia as the default country
                  international // Show international format with country code
                  countryCallingCodeEditable={false} // Prevent editing the country code
                  className="custom-phone-input" // Custom class for styling consistency
                  placeholder="Enter phone number"
                  // Add maxLength to the underlying input for extra safety
                  inputprops={{
                    maxLength: 16, // Approximate max length to account for formatting (+, spaces)
                  }}
                  onKeyDown={(e) => {
                    // Prevent typing if digit count would exceed 12
                    const inputValue = e.target.value || "";
                    const digitCount = inputValue.replace(/[^0-9]/g, "").length;
                    if (
                      digitCount >= 12 &&
                      e.key !== "Backspace" &&
                      e.key !== "Delete" &&
                      e.key !== "ArrowLeft" &&
                      e.key !== "ArrowRight" &&
                      e.key !== "Tab"
                    ) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    // Prevent pasting if the resulting digits would exceed 12
                    const pastedData = e.clipboardData.getData("text");
                    const newDigitCount = (value + pastedData).replace(
                      /[^0-9]/g,
                      ""
                    ).length;
                    if (newDigitCount > 12) {
                      e.preventDefault();
                    }
                  }}
                />
              )}
            />
            {errors.currentContact?.mobileNumber && (
              <small className="text-danger">
                {errors.currentContact.mobileNumber.message}
              </small>
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label form-label-new">
              Guardian Email Address
              <span className="text-danger fs-9">*</span>
            </label>
            <input
              {...register("currentContact.email", {
                required: "This field is required.",
                maxLength: {
                  value: 75,
                  message: "Maximum of 75 characters allowed.",
                },
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: "Please enter a valid email.",
                },
              })}
              type="text"
              className="form-control"
            />
            {errors.currentContact?.email && (
              <small className="text-danger">
                {errors.currentContact.email.message}
              </small>
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label form-label-new">
              Country <span className="text-danger fs-9">*</span>
            </label>
            <select
              className="form-select"
              {...register("currentContact.countryId", {
                required: "This field is required.",
              })}
            >
              <option value="">Select</option>
              {countries.map((country, index) => (
                <option key={index} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
            {errors.currentContact?.countryId && (
              <small className="text-danger">
                {errors.currentContact.countryId.message}
              </small>
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label form-label-new">
              State<span className="text-danger fs-9">*</span>
            </label>
            <input
              {...register("currentContact.stateName", {
                required: "This field is required.",
              })}
              type="text"
              className="form-control"
            />
            {errors.currentContact?.stateName && (
              <small className="text-danger">
                {errors.currentContact.stateName.message}
              </small>
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label form-label-new">
              City<span className="text-danger fs-9">*</span>
            </label>
            <input
              {...register("currentContact.cityName", {
                required: "This field is required.",
              })}
              type="text"
              className="form-control"
            />
            {errors.currentContact?.cityName && (
              <small className="text-danger">
                {errors.currentContact.cityName.message}
              </small>
            )}
          </div>
          <div className="col-md-3">
            <div>
              <label className="form-label form-label-new">
                District<span className="text-danger fs-9">*</span>
              </label>
              <input
                {...register("currentContact.district", {
                  required: "This field is required.",
                })}
                type="text"
                className="form-control"
              />
              {errors.currentContact?.district && (
                <small className="text-danger">
                  {errors.currentContact.district.message}
                </small>
              )}
            </div>
          </div>

          <div className="col-md-3">
            <label className="form-label form-label-new">
              Address<span className="text-danger fs-9">*</span>
            </label>
            <textarea
              {...register("currentContact.address", {
                required: "This field is required.",
                maxLength: {
                  value: 250,
                  message: "Maximum of 250 characters allowed.",
                },
              })}
              type="text"
              className="form-control"
              rows={1}
            ></textarea>
            {errors.currentContact?.address && (
              <small className="text-danger">
                {errors.currentContact.address.message}
              </small>
            )}
          </div>
          {/* <div className="m-0"></div> */}
          <div className="col-md-3">
            <Controller
              name={`currentContact.isPrimary`}
              control={control}
              render={({ field }) => (
                <div className="form-check">
                  <input
                    {...field}
                    type="checkbox"
                    className="form-check-input"
                    id={`isPrimary`}
                    checked={field.value || false}
                  />
                  <label className="form-check-label" htmlFor={`isPrimary`}>
                    Primary Contact
                  </label>
                  {errors.currentContact?.isPrimary && (
                    <small className="text-danger">
                      {errors.currentContact.isPrimary.message}
                    </small>
                  )}
                </div>
              )}
            />
          </div>
        </div>

        <div className="mt-4">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={async () => {
              await save();
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <span
                className="spinner-border spinner-custom-size me-1"
                aria-hidden="true"
              ></span>
            ) : null}
            Save Contacts
          </button>
        </div>
      </div>

      {isCustomDropdownValuesModalVisible &&
        createPortal(
          <DropdownWithCustomValuesModal
            dropdownName={customDropdownName}
            onClose={() => {
              setIsCustomDropdownValuesModalVisible(false);
              if (customDropdownName === "title") {
                fetchCommonSettings("title", setTitleData);
              } else if (customDropdownName === "relationShip") {
                fetchCommonSettings("relationShip", setRelationshipData);
              } else if (customDropdownName === "nationality") {
                fetchCommonSettings("nationality", setNationalityData);
              }
            }}
          />,
          document.body // or a #modal-root div
        )}

      <StepSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onAddAnother={handleModalAddAnother}
        onNavigate={handleModalNavigateToPupils}
        message="Contacts Saved Successfully!"
        nextButtonText="Go to Pupils"
      />

      <RegStepItemDeleteInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        message="Contact removed from the list. Click 'Save Contacts' to delete it permanently."
      />
    </div>
  );
});

export default Step1Contacts;
